import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FormikProvider, useFormik } from 'formik'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { POST_CREATE_EMPLOYEE_KEY, postCreateEmployee } from '../payrollActions'
import { getMinDateOfBirth } from '../helpers'
import { SubscriptionIncrease } from './SubscriptionIncrease'
import { PayrollError } from '../PayrollError'
import {
  Card,
  Button,
  Text,
  Link,
  Alert,
  Separator,
  Form,
  Icon,
  Dialog,
} from '@/ui'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  makeDateSchema,
  makeReqStringSchema,
  getFieldName,
  makeReqEmailSchema,
} from '@/componentsV1/BaseComponents'
const VAL_END = 'for this employee'

export const CreateEmployee = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  const maxDateOfBirth = getMinDateOfBirth()

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      dob: '',
      email: '',
    },
    // This is a little unusual but the modal does the submission portion.  It won't open unless validation succeeds
    onSubmit: () => setModalOpen(true),
  })

  const onSave = useCallback(async () => {
    const employee = await postCreateEmployee({
      first_name: formik.values.firstName,
      last_name: formik.values.lastName,
      date_of_birth: moment(formik.values.dob).format(
        DATE_FORMATS.GUSTO_SUBMIT
      ),
      email: formik.values.email,
      self_onboarding: true,
    })(dispatch)

    if (employee) {
      navigate(`/payroll/create_employee/job/${employee.uuid}`)
    }
  }, [
    dispatch,
    formik.values.dob,
    formik.values.email,
    formik.values.firstName,
    formik.values.lastName,
    navigate,
  ])

  return (
    <Card>
      <FormikProvider value={formik}>
        <div className="flex flex-col gap-6">
          <Text variant={TypographyStyles.H1}>Add a new team member</Text>

          <Alert>
            Please enter basic information about your new team member. They will
            receive an email with instructions to complete their onboarding.
          </Alert>

          <Text variant={TypographyStyles.H2}>Enter Employee Information</Text>

          <Separator />

          <Text>
            Prior to an employee’s first day of work, employers are legally
            required to verify their eligibility to work using{' '}
            <Link
              href="https://www.uscis.gov/sites/default/files/document/forms/i-9-paper-version.pdf"
              newPage
            >
              Form I-9
            </Link>
            . For more information, click{' '}
            <Link href="https://www.uscis.gov/i-9" newPage>
              here.
            </Link>
          </Text>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-1/2">
              <Form.Input
                name={getFieldName<typeof formik.values>('firstName')}
                label="First name"
                description="Their official, legal name."
                required
                schema={makeReqStringSchema({
                  field: 'first name',
                  end: VAL_END,
                })}
              />
            </div>
            <div className="w-full md:w-1/2">
              <Form.Input
                name={getFieldName<typeof formik.values>('lastName')}
                label="Last name"
                description="Their official, legal name."
                required
                schema={makeReqStringSchema({
                  field: 'last name',
                  end: VAL_END,
                })}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-1/2">
              <Form.DateInput
                name={getFieldName<typeof formik.values>('dob')}
                maxDate={maxDateOfBirth.toJSDate()}
                label="Date of Birth"
                required
                schema={makeDateSchema({
                  field: 'date of birth',
                  end: VAL_END,
                  maxDate: maxDateOfBirth.toFormat(DATE_FORMATS_LUXON.INPUT),
                })}
              />
            </div>
            <div className="w-full md:w-1/2">
              <Form.Input
                name={getFieldName<typeof formik.values>('email')}
                type="email"
                label="Email"
                required
                schema={makeReqEmailSchema({ field: 'email', end: VAL_END })}
              />
            </div>
          </div>

          <Text>
            This employee will be invited to enter their own details in Gusto.
          </Text>

          <div className="flex flex-col md:flex-row justify-between gap-4 pt-2">
            <div className="w-full md:w-1/4">
              <Button
                variant="secondary"
                onClick={() => navigate('/payroll/employees')}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
            <div className="hidden md:block md:w-1/2" />
            <div className="w-full md:w-1/4">
              <Button onClick={formik.submitForm} className="w-full">
                Add Employee
                <Icon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </FormikProvider>
      <Dialog open={modalOpen} onOpenChange={() => setModalOpen(false)}>
        <Dialog.Content className="max-w-sm space-y-6">
          <Dialog.Title>
            Confirm New Employee: {formik.values.firstName}{' '}
            {formik.values.lastName}
          </Dialog.Title>

          <Text>
            You are about to add a new member to your team! Just confirming a
            couple of details.
          </Text>

          <Text>
            Once confirmed, your new employee will be asked to fill out a form
            to complete the setup process.
          </Text>

          <PayrollError fetchKey={POST_CREATE_EMPLOYEE_KEY} />

          <Text>Full Legal Name</Text>
          <Text>
            {formik.values.firstName} {formik.values.lastName}
          </Text>

          <Text className="text-green-600 mb-1">
            <b>Price increase</b>
          </Text>
          <Text className="text-green-600">
            <SubscriptionIncrease />
          </Text>

          <div className="flex justify-end gap-4 pt-4">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSave}>Add Employee</Button>
          </div>
        </Dialog.Content>
      </Dialog>
    </Card>
  )
}
